import { memo, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useSetSimProfile, useSimProfiles } from "@/hooks/useSims";
import { hasRatePlanProfile, type SimCard } from "@/lib/sims";
import { SimProfileSelect } from "./SimProfileSelect";

/** Give selected SIMs a profile by hand (SIMs on a profile rate plan are skipped). */
export const SetSimProfileDialog = memo(function SetSimProfileDialog({
  open,
  onOpenChange,
  sims,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sims: SimCard[];
  onDone: () => void;
}) {
  const { data: profiles } = useSimProfiles();
  const setProfile = useSetSimProfile();
  const [profileId, setProfileId] = useState<string | null>(null);
  useEffect(() => {
    if (open) setProfileId(null);
  }, [open]);

  const profile = profiles.find((p) => p.id === profileId) ?? null;
  const { fixed, otherProvider, changeable } = useMemo(() => {
    const fixed = sims.filter((s) => hasRatePlanProfile(s, profiles));
    const rest = sims.filter((s) => !hasRatePlanProfile(s, profiles));
    const otherProvider = profile
      ? rest.filter((s) => s.provider !== profile.provider)
      : [];
    return {
      fixed,
      otherProvider,
      changeable: rest.length - otherProvider.length,
    };
  }, [sims, profiles, profile]);
  const providers = useMemo(
    () => Array.from(new Set(sims.map((s) => s.provider))),
    [sims],
  );

  const submit = () =>
    setProfile.mutate(
      { simIds: sims.map((s) => s.id), profileId },
      {
        onSuccess: () => {
          onDone();
          onOpenChange(false);
        },
      },
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="set-sim-profile-dialog">
        <DialogHeader>
          <DialogTitle>
            Set profile for {sims.length} SIM{sims.length === 1 ? "" : "s"}
          </DialogTitle>
          <DialogDescription>
            Only changes the profile recorded here, not the SIM at the carrier.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label>Profile</Label>
          <SimProfileSelect
            profiles={profiles}
            providers={providers}
            value={profileId}
            onChange={setProfileId}
            testId="set-sim-profile-select"
          />
        </div>
        {(fixed.length > 0 || otherProvider.length > 0) && (
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {fixed.length > 0 && (
              <li>
                {fixed.length} SIM{fixed.length === 1 ? " is" : "s are"} on a
                rate plan that selects the profile and will be skipped.
              </li>
            )}
            {otherProvider.length > 0 && (
              <li>
                {otherProvider.length} SIM
                {otherProvider.length === 1 ? " is" : "s are"} from another
                provider than {profile?.name} and will be skipped.
              </li>
            )}
          </ul>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={changeable === 0 || setProfile.isPending}
            data-testid="confirm-set-sim-profile"
          >
            {setProfile.isPending
              ? "Saving…"
              : profileId
                ? `Set on ${changeable} SIM${changeable === 1 ? "" : "s"}`
                : `Clear on ${changeable} SIM${changeable === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
